import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Smartphone, Clock, TrendingUp, MessageCircle } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { WhatsFab } from "@/components/site/WhatsFab";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { waLink } from "@/lib/brand";

const MSG = "Olá! Quero ser revendedor Speed Max e começar a faturar pelo celular. Pode me passar as informações?";

const PERKS = [
  { icon: Smartphone, t: "Só com o celular", d: "Venda, atenda e acompanhe seus clientes pelo WhatsApp, de onde estiver." },
  { icon: Clock, t: "No seu horário", d: "Você decide quando trabalhar. Renda extra ou principal, quem escolhe é você." },
  { icon: TrendingUp, t: "Mais de R$ 1.500/mês", d: "Revendendo os servidores Speed Max, você pode faturar mais de R$ 1.500 por mês." },
];

const STEPS = ["Chame a gente no WhatsApp", "Receba todas as informações", "Comece a vender e faturar"];

const Phone = () => (
  <motion.div
    initial={{ opacity: 0, y: 60, rotate: 6 }}
    animate={{ opacity: 1, y: 0, rotate: -4 }}
    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
    className="relative mx-auto w-[250px] sm:w-[290px] aspect-[9/19] rounded-[42px] border border-white/15 bg-[#0e1120] p-3 shadow-[0_50px_140px_-30px_rgba(139,92,246,.6)]"
  >
    <div className="h-full w-full overflow-hidden rounded-[32px] bg-gradient-to-b from-[#1a1440] to-[#07080f] p-4 pt-8">
      <p className="font-display text-[11px] tracking-[0.3em] text-zinc-400">SEU MÊS</p>
      <p className="mt-2 font-display text-4xl font-black grad-text">R$ 1.500+</p>
      <div className="mt-6 space-y-3">
        {["Novo cliente · Opção 2", "Teste solicitado · Opção 1", "Renovação · Opção 5", "Novo cliente · Opção 4"].map((t, i) => (
          <motion.div key={t} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2 + i * 0.25, duration: 0.7 }} className="flex items-center gap-3 rounded-2xl bg-white/[0.07] p-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#25D366]"><MessageCircle className="h-4 w-4 text-white" /></span>
            <span className="text-xs text-zinc-200">{t}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

export default function Revendedor() {
  useLenis();
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = "Seja um revendedor · Speed Max";
    return () => { document.title = prev; };
  }, []);

  return (
    <main data-testid="revendedor-page" className="bg-[#07080f] text-white overflow-x-clip">
      <Nav base="/" />
      <section className="grain relative overflow-hidden pt-28 sm:pt-40 pb-20 sm:pb-32">
        <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#3b2bd1]/30 blur-[140px]" />
        <div className="pointer-events-none absolute right-0 bottom-0 h-[420px] w-[420px] rounded-full bg-cyan-500/15 blur-[140px]" />
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-16 px-5 sm:px-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="mb-8 flex items-center gap-3 text-xs tracking-[0.35em] text-cyan-300"><span className="h-px w-10 bg-cyan-300" />SEJA UM REVENDEDOR</p>
            <h1 data-testid="revendedor-title" className="font-display font-extrabold tracking-tight leading-[1] text-[8.5vw] sm:text-5xl lg:text-[4.1rem]">
              <span className="lg:hidden">
                <MaskLines lines={["Fature mais"]} />
                <MaskLines lines={["de R$ 1.500", "por mês"]} delay={0.12} lineClass="grad-text" />
                <MaskLines lines={["só com o celular."]} delay={0.36} />
              </span>
              <span className="hidden lg:block">
                <MaskLines lines={["Fature mais de"]} />
                <MaskLines lines={["R$ 1.500 por mês"]} delay={0.12} lineClass="grad-text" />
                <MaskLines lines={["só com o celular."]} delay={0.24} />
              </span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="mt-8 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">
              Sem loja, sem estoque, sem sair de casa. Revenda os servidores da Speed Max — no mercado desde 2017 — e transforme o seu WhatsApp em uma fonte de renda.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1 }} className="mt-10 flex flex-wrap gap-3">
              <a data-testid="revendedor-cta-hero" href={waLink(MSG)} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-3 rounded-full grad-bg px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
                Quero ser revendedor <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-4"><Phone /></div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-10 py-20 sm:py-32">
        <Reveal><h2 className="max-w-3xl font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">Seu celular já é <span className="grad-text">a sua empresa.</span></h2></Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {PERKS.map(({ icon: I, t, d }, i) => (
            <Reveal key={t} delay={i * 0.1} className="h-full">
              <motion.div data-testid={`revendedor-perk-${i}`} whileHover={{ y: -6 }} className="h-full rounded-[24px] border border-white/10 bg-[#0e1120] p-8">
                <I className="h-8 w-8 text-cyan-300" strokeWidth={1.5} />
                <h3 className="mt-8 font-display text-xl font-bold">{t}</h3>
                <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed">{d}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0a0b15]">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 sm:px-10 py-20 sm:py-28 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><p className="text-xs tracking-[0.35em] text-cyan-300">COMO COMEÇAR</p><h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight">3 passos.<br />Só isso.</h2></Reveal>
          <ol className="lg:col-span-7 divide-y divide-white/10">
            {STEPS.map((s, i) => (
              <Reveal key={s} delay={i * 0.1}>
                <li className="flex items-center gap-6 py-6"><span className="font-display text-5xl font-black outline-num w-24 shrink-0">0{i + 1}</span><span className="font-display text-xl sm:text-2xl font-bold">{s}</span></li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="grain relative overflow-hidden py-24 sm:py-36 text-center">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8b5cf6]/25 blur-[140px]" />
        <Reveal className="relative mx-auto max-w-3xl px-5">
          <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1]">A próxima renda extra<br /><span className="grad-text">pode ser a sua.</span></h2>
          <a data-testid="revendedor-cta-final" href={waLink(MSG)} target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-3 rounded-full bg-white px-9 py-5 text-sm font-semibold text-[#07080f] transition-transform duration-300 hover:scale-105">
            <MessageCircle className="h-5 w-5" /> Falar no WhatsApp agora
          </a>
        </Reveal>
      </section>
      <Footer />
      <WhatsFab />
    </main>
  );
}
